import {
    Component,
    OnInit,
    Output,
    Input,
    EventEmitter,
    ViewChild,
    ElementRef
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Job, JobStatus, JobType } from '../job.types';
import { Client } from '@intranet/modules/clients/client.types';
import { User } from '@intranet/modules/users/user.types';
import { JobService } from '../job.service';
import { ClientsService } from '@intranet/modules/clients';
import { UserService } from '@intranet/modules/users/user.service';
import { LoginService } from '@intranet/modules/login/login.service';
import { orderClientsByGroupName } from '@intranet/modules/clients/client.helpers';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { JobAction, ActionStatus } from '@intranet/modules/actions';
import { ActionService } from '@intranet/modules/actions/action.service';
import { HotkeyService } from 'src/app/core/services/hotkey.service';

@Component({
    selector: 'jobs-copy',
    templateUrl: './jobs-copy.component.html',
    styleUrls: ['./jobs-copy.component.scss']
})
export class JobsCopyComponent implements OnInit {
    @Input() originalJob: Job;
    @Output() cancel = new EventEmitter<void>();
    @Output() jobCopied = new EventEmitter<number>(); // used for "Copy and Close"
    @Output() jobCopiedAndOpen = new EventEmitter<number>(); // used for "Copy and Open"

    @ViewChild('jobForm') jobForm: NgForm;
    @ViewChild('jobTitleField') jobTitleField: ElementRef;

    newJob: Job;
    jobTypes: JobType[] = [];
    users: User[] = [];
    filterClients: [Client[], Client[]] = [[], []];
    allClients: Client[] = [];

    selectedActions: JobAction[] = [];
    submitting = false;
    error = '';
    currentJobId: number;
    hasAnyActions: boolean = false;
    hasCheckedActionsOnce: boolean = false;

    constructor(
        private jobService: JobService,
        private clientService: ClientsService,
        private userService: UserService,
        private loginService: LoginService,
        private actionService: ActionService,
        private hotkeyService: HotkeyService,
    ) {}

    ngOnInit(): void {
        // Register Ctrl+Q to cancel the modal
        this.hotkeyService.registerHotkey('ctrl+q', 'Close Copy Job modal', () => this.onCancel());

        combineLatest([
            this.jobService.jobTypes$,
            this.clientService.clients$,
            this.clientService.myClients$,
            this.userService.users$
        ])
            .pipe(take(1))
            .subscribe(([jobTypes, allClients, myClients, users]) => {
                this.jobTypes = jobTypes;
                this.allClients = allClients;
                this.users = users.filter((u) => u.active);

                const sorted = orderClientsByGroupName(
                    allClients,
                    myClients.map((c) => c.clientId),
                    true
                );

            const pinnedClients = Array.isArray(sorted[0]) ? sorted[0] : [sorted[0]];
            const restClients = Array.isArray(sorted[1]) ? sorted[1] : [sorted[1]];
            this.filterClients = [pinnedClients, restClients];

            this.newJob = {
                jobId: null,
                jobTypeId: this.originalJob.jobType?.jobTypeId,
                jobType: this.originalJob.jobType,
                jobNumber: 0,
                clientId: this.originalJob.clientId,
                client: this.originalJob.client,
                jobTitle: '',
                jobOwnerId: this.originalJob.jobOwnerId,
                jobOwner: this.originalJob.jobOwner,
                jobStatus: JobStatus.Live,
                active: true,
                archive: false,
                deliveryDate: null,
                dateCreated: new Date().toISOString(),
                dateArchived: null,
                firstTimeRecordedDate: null,
                jobNoOR: 0,
                jobActions: [],
                liveActionsDue: false,
                liveActionsOverdue: false,
                hasScheduledActions: false,
                canArchive: false,
                togglProjectId: null,
                isBillable: false,
                rocks: this.originalJob.rocks,
                marginValueStatus: null
            };

            this.hasAnyActions = this.originalJob.jobActions?.length > 0;
        });
    }

    ngAfterViewInit(): void {
        this.jobTitleField?.nativeElement?.focus();
    }

    onActionsSelected(actions: JobAction[]) {
        this.selectedActions = actions;

        // Check once whether the job has any actions
        if (!this.hasCheckedActionsOnce) {
            this.hasAnyActions = actions.length > 0 || this.originalJob.jobActions?.length > 0;
            this.hasCheckedActionsOnce = true;
        }
    }

    onSubmit(shouldRedirect: boolean): void {
        if (this.submitting || !this.newJob || !this.selectedActions.length) return;
        this.submitting = true;
        this.error = '';

        const payload = {
            jobStatus: JobStatus.Live,
            jobTypeId: this.newJob.jobTypeId,
            jobType: {},
            active: true,
            jobTitle: this.newJob.jobTitle,
            jobOwnerId: this.newJob.jobOwnerId,
            jobOwner: this.newJob.jobOwner,
            clientId: this.newJob.clientId,
            jobActions: []
        };

        this.jobService.addOrUpdateJob(payload as Job).subscribe({
            next: (result) => {
                if (result.success && result.job?.jobId) {
                    this.currentJobId = result.job.jobId;
                    this.copyActions(result.job.jobId, shouldRedirect);
                } else {
                    this.error = result.error || 'Failed to create job.';
                    this.submitting = false;
                }
            },
            error: (err) => {
                this.error = 'An error occurred while creating the job.';
                console.error(err);
                this.submitting = false;
            }
        });
    }

    private copyActions(jobId: number, closeOnly: boolean) {
        const liveActions = this.selectedActions.filter(
            a => a.actionStatus !== ActionStatus.Completed && a.actionStatus !== ActionStatus.Archived
        );

        const copyRequests = liveActions.map(originalAction => {
            return this.actionService.create({
                jobId,
                isDeleted: false,
                jobActionType: originalAction.jobActionType,
                actionStatus: originalAction.actionStatus,
                scheduleDate: originalAction.scheduleDate,
                deadlineDate: originalAction.deadlineDate,
                assignedUsers: [...(originalAction.assignedUsers ?? [])],
                assignedGroups: [...(originalAction.assignedGroups ?? [])],
                waitingUserId: originalAction.waitingUserId ?? null,
                waitingGroupId: originalAction.waitingGroupId ?? null,
                priority: originalAction.priority ?? 0,
                title: originalAction.title,
                jiraLinks: (originalAction.jiraLinks ?? []).map(link => ({
                    jiraLinkId: 0,
                    jobActionId: 0,
                    url: link.url,
                    title: link.title,
                    board: link.board || '',
                    issue: link.issue || '',
                    isIssue: !!link.isIssue
                }))
            } as JobAction);
        });

        Promise.all(copyRequests.map(req => req.toPromise()))
            .then(() => {
                if (closeOnly) {
                    this.jobCopied.emit(this.currentJobId); // emit for close-only
                } else {
                    this.jobCopiedAndOpen.emit(this.currentJobId); // emit for redirect
                }
                this.cancel.emit(); // always close the modal after copy
            })
            .catch(err => {
                this.error = 'Failed to copy one or more actions.';
                console.error(err);
            })
            .finally(() => {
                this.submitting = false;
            });
    }


    onCancel(): void {
        this.cancel.emit();
    }

    get canSubmit(): boolean {
        return this.jobForm?.valid && this.selectedActions.length > 0 && !this.submitting;
    }
}
